import { FC, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox } from '@eneco/sparky';
import { Products_Offers_V3_OfferUsagesResponseModel } from '@monorepo-types/dc';
import { NotificationField } from '@sitecore/types/OfferStep';

import { FlowConsumerContextEnergy } from '../../../context';

interface Props {
  flowContext: FlowConsumerContextEnergy;
  usageData?: Products_Offers_V3_OfferUsagesResponseModel;
  redeliveryMoreNotificationTextualData?: NotificationField;
  redeliveryLessNotificationTextualData?: NotificationField;
}

export const PriceDetailsOverviewRedeliveryNotification: FC<Props> = ({
  flowContext,
  usageData,
  redeliveryMoreNotificationTextualData,
  redeliveryLessNotificationTextualData,
}): JSX.Element => {
  const { hasSolarPanels } = flowContext;

  const {
    electricityDeliveryNormal = 0,
    electricityDeliveryLow = 0,
    electricityRedeliveryNormal = 0,
    electricityRedeliveryLow = 0,
    electricityRedeliveryRemainder = 0,
  } = usageData || {};

  const { format, number } = useFormatter();

  const electricityTotal = Number(electricityDeliveryNormal) + Number(electricityDeliveryLow);
  const electricityRedeliveryTotal = electricityRedeliveryNormal + electricityRedeliveryLow;

  // Shared by both notifications below; "less than usage" copy doesn't reference the remainder placeholder.
  const formatValues = {
    electricityTotal: String(number.noFractionDigits(electricityTotal)),
    electricityRedelivery: String(number.noFractionDigits(electricityRedeliveryTotal)),
    electricityRedeliveryRemainder: String(number.noFractionDigits(electricityRedeliveryRemainder ?? 0)),
  };

  const notifications = [
    {
      type: 'more' as const,
      show:
        !!hasSolarPanels &&
        electricityRedeliveryTotal > electricityTotal &&
        !!redeliveryMoreNotificationTextualData?.value?.content,
      textualData: redeliveryMoreNotificationTextualData,
    },
    {
      type: 'less' as const,
      show:
        !!hasSolarPanels &&
        electricityRedeliveryTotal <= electricityTotal &&
        !!redeliveryLessNotificationTextualData?.value?.content,
      textualData: redeliveryLessNotificationTextualData,
    },
  ];

  return (
    <>
      {notifications.map(
        ({ type, show, textualData }) =>
          show && (
            <NotificationBox
              key={type}
              isAlert={false}
              variant={textualData?.value?.variant}
              title={textualData?.value?.title}
              text={<RichText html={format(textualData?.value?.content ?? '', formatValues)} />}
            />
          ),
      )}
    </>
  );
};
